export const uuid = (): string => {
  if (window.crypto != null && window.crypto.randomUUID != null) {
    return window.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function searchParams(search: string): URLSearchParams {
  if (search === "" || search == null) {
    return new URLSearchParams();
  }
  return new URLSearchParams(search);
}

export function mapValues<
  Obj extends Record<string, Values>,
  Values extends Record<string, unknown>,
  ValueKey extends keyof Values,
>(obj: Obj, key: ValueKey): Record<keyof Obj, Obj[keyof Obj][ValueKey]> {
  type ResultType = Record<keyof Obj, Obj[keyof Obj][ValueKey]>;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const result = {} as ResultType;
  return Object.entries(obj).reduce<ResultType>((agg, [name, value]) => {
    const nameKey = name as keyof Obj;
    agg[nameKey] = value[key] as Obj[keyof Obj][ValueKey];
    return agg;
  }, result);
}
export function getCurrentPath(hashMode: boolean): string {
  const location = window.location;
  return hashMode && location.hash.length > 0
    ? `${location.pathname}${location.hash}`
    : location.pathname;
}

export function safeJSONParse(val: string, def?: unknown): unknown {
  try {
    return JSON.parse(val);
  } catch (e) {
    return def;
  }
}

export function safeJSONStringify(val: unknown): string | undefined {
  try {
    return JSON.stringify(val);
  } catch (e) {}
}

export function ready(fn: () => void, wait: number = 0): void {
  if (document.readyState !== "loading") {
    setTimeout(fn, wait); // simulate async
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

/**
 * Executes a callback when an iframe's document is ready.
 * Safely handles cross-origin iframes which cannot be accessed.
 */
export function iframeready(
  iframe: HTMLIFrameElement,
  fn: (_doc: Document) => void
): void {
  try {
    // Check if we can access the iframe's document (same-origin check)
    if (iframe.contentDocument != null) {
      fn(iframe.contentDocument);
    } else {
      iframe.addEventListener("load", () => {
        try {
          // Re-check after load - contentDocument may now be available
          if (iframe.contentDocument != null) {
            fn(iframe.contentDocument);
          }
        } catch {
          // Cross-origin iframe after load - silently skip
        }
      });
    }
  } catch {
    // Cross-origin iframe - cannot access contentDocument
    // This is expected and not an error - silently skip
  }
}
export function isFormElement(node: Node): node is HTMLFormElement {
  if (node.nodeName.toLowerCase() === "form") {
    return true;
  }
  return false;
}

export type FormInputField =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement;
export function isFormInputElement(node: Node): node is FormInputField {
  const nodeName = node.nodeName.toLowerCase();
  return ["input", "textarea", "select"].includes(nodeName);
}

export function setDataAttribute(
  selector: string,
  key: string,
  value: string
): void {
  const element = document.querySelector(selector);
  if (element != null) {
    element.setAttribute(`data-${key}`, value);
  }
}
